import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { BossBabyTaskDto } from '@/dto/BossBabyDto';

interface TaskCardProps {
  task: BossBabyTaskDto;
  onEdit: (taskId: number) => void;
  onDelete: (taskId: number) => void;
}

export const TaskCard: React.FC<TaskCardProps> = React.memo(({ 
  task, 
  onEdit, 
  onDelete 
}) => {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'error';
      case 'MEDIUM':
        return 'warning';
      case 'LOW':
        return 'info';
      default:
        return 'default';
    }
  };

  const getFrequencyColor = (frequency: string) => {
    switch (frequency) {
      case 'DAILY':
        return 'success';
      case 'WEEKLY':
        return 'info';
      case 'MONTHLY':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Card 
      variant="outlined" 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          boxShadow: 2,
        },
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon color="primary" />
            <Typography variant="h6" component="span">
              {task.taskName}
            </Typography>
          </Box>
        }
        action={
          <Box>
            <Tooltip title="Edit Task">
              <IconButton 
                size="small" 
                onClick={() => onEdit(task.taskId)}
                color="primary"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Task">
              <IconButton 
                size="small" 
                onClick={() => onDelete(task.taskId)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
        sx={{ pb: 1 }}
      />
      
      <CardContent sx={{ flexGrow: 1, pt: 0 }}>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 2, 
            minHeight: '40px',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {task.description}
        </Typography>

        {/* Tools Used */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Tools Used:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {task.tools.slice(0, 3).map((tool, index) => (
              <Chip 
                key={index}
                label={tool} 
                size="small" 
                variant="outlined"
                color="secondary"
                sx={{ fontSize: '0.75rem', height: '20px' }}
              />
            ))}
            {task.tools.length > 3 && (
              <Chip 
                label={`+${task.tools.length - 3} more`}
                size="small" 
                variant="outlined"
                color="default"
                sx={{ fontSize: '0.75rem', height: '20px' }}
              />
            )}
          </Box>
        </Box>

        {/* Example Message */}
        {task.exampleMessage && (
          <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Example:
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontStyle: 'italic',
                fontSize: '0.875rem',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              "{task.exampleMessage}"
            </Typography>
          </Box>
        )}

        {/* Status Chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip 
            label={task.category} 
            size="small" 
            variant="outlined"
            color="secondary"
          />
          <Chip 
            label={task.priorityLevel} 
            size="small" 
            color={getPriorityColor(task.priorityLevel) as any}
          />
          <Chip 
            label={task.executionFrequency} 
            size="small" 
            color={getFrequencyColor(task.executionFrequency) as any}
            variant="outlined"
          />
          <Chip 
            label={task.isActive ? 'Active' : 'Inactive'} 
            size="small" 
            color={task.isActive ? 'success' : 'error'}
            variant={task.isActive ? 'filled' : 'outlined'}
          />
        </Box>

        {/* Metadata */}
        <Typography variant="caption" color="text.secondary" display="block">
          Created: {formatDate(task.createdAt)}
        </Typography>
        
        {task.updatedAt !== task.createdAt && (
          <Typography variant="caption" color="text.secondary" display="block">
            Updated: {formatDate(task.updatedAt)}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
});